import { Typography } from '@mui/material';
import { useState } from 'react';

import { useCreateMailpiecesListCommentMutation, useUpdatePiecesMutation } from '@api/piece';
import { PieceAction } from '@apiContract';

import { clearRecipientFilter } from '@redux/slices/recipientFilter';
import { useDispatch, useSelector } from '@redux/store';

import { ActionComponentProps } from '../../index';
import { buildValidationPieces, getItemsCount } from '../../utils';
import { DialogQrCodeConfirmation } from '../DialogQrCodeConfirmation';
import { ActionWithInfo } from './ActionWithInfo';

export const FinishPickUpWithQrCode = ({
  mailpieces,
  actionStarted,
  onActionFinish,
  validationData,
}: ActionComponentProps) => {
  const dispatch = useDispatch();
  const [updatePieces] = useUpdatePiecesMutation();
  const [createMailpiecesListComment] = useCreateMailpiecesListCommentMutation();
  const [actionInProgress, setActionInProgress] = useState<boolean>(false);

  const { qrCode: prefilledQrCode } = useSelector((state) => state.recipientFilter);

  const count = getItemsCount(mailpieces);

  const confirmWithQrCode = async (
    qrCodeData: string,
    skipNotifyRecipient: boolean,
    comment: string,
    notifyMailroom: boolean,
  ) => {
    setActionInProgress(true);

    try {
      await updatePieces({
        pieces: buildValidationPieces(mailpieces, validationData),
        action: PieceAction.FinishPickUpWithQrCode,
        qrCode: qrCodeData,
        shouldNotifyRecipient: !skipNotifyRecipient,
      }).unwrap();

      if (comment.trim().length) {
        await createMailpiecesListComment({
          text: comment.trim(),
          notifyMailroom,
          pieceIds: mailpieces.map((piece) => piece.id),
        }).unwrap();
      }

      dispatch(clearRecipientFilter());
    } finally {
      setActionInProgress(false);
    }
  };

  return (
    <ActionWithInfo
      open={actionStarted}
      title={
        prefilledQrCode ? (!count ? 'Complete pickup' : 'Complete pickup for selected items') : 'Complete with QR Code'
      }
      onClose={onActionFinish}
      validationData={validationData}
      acceptButtonTitle={prefilledQrCode ? 'Complete' : 'Scan QR Code'}
      loading={actionInProgress}
      onAccept={
        prefilledQrCode
          ? async ({ skipNotifyRecipient, comment, notifyMailroom }) => {
              try {
                await confirmWithQrCode(prefilledQrCode, skipNotifyRecipient, comment, notifyMailroom);
                onActionFinish();
              } catch {
                dispatch(clearRecipientFilter());
              }
            }
          : undefined
      }
      infoContent={
        <Typography variant="body1" color="text.secondary">
          {prefilledQrCode ? (
            !count ? (
              <>Please give this item to the recipient, and click Complete when you&apos;re done.</>
            ) : (
              <>
                Please give <strong>{mailpieces.length}</strong> items to the recipient, and click Complete when
                you&apos;re done.
              </>
            )
          ) : count > 1 ? (
            `Please scan the recipient’s QR code for ${count} items.`
          ) : (
            'Please scan the recipient’s QR code for this item.'
          )}
        </Typography>
      }
    >
      {({
        step,
        skipNotifyRecipient,
        comment,
        notifyMailroom,
        onClose,
        onFinish,
        onBackToInfo,
      }) => (
        <DialogQrCodeConfirmation
          mailpieces={mailpieces}
          actionStarted={actionStarted && step === 'action'}
          actionRequestInProgress={actionInProgress}
          onActionFinish={onClose}
          onQrCodeRecognized={async (qrCodeData) => {
            try {
              await confirmWithQrCode(qrCodeData, skipNotifyRecipient, comment, notifyMailroom);
              onFinish();
            } catch {
              onBackToInfo('The scanned QR code does not match the recipient');
            }
          }}
        />
      )}
    </ActionWithInfo>
  );
};
